/**
 * BreachNotificationPanel Component
 *
 * Displays comprehensive breach notification requirements for the selected
 * threat profile, including:
 * - Timeline badge
 * - Numbered notification sequence
 * - Primary/secondary authority citations
 * - Collapsible state preemption notes
 * - State-specific callout based on client location
 */

import { useState } from 'react'

/**
 * Determine urgency level from a notification timeline string.
 * Returns 'urgent' (hours/immediately), 'moderate' (days up to ~60), or 'flexible' (per policy/state/contractual).
 */
function getTimelineUrgency(timeline) {
  if (!timeline) return 'flexible'
  const t = timeline.toLowerCase()
  if (t.includes('immediate') || t.includes('hour')) return 'urgent'
  if (t.includes('per ') || t.includes('contractual') || t.includes('varies')) return 'flexible'
  // Try to extract numeric day values
  const dayMatch = t.match(/(\d+)\s*days?/)
  if (dayMatch) {
    const days = parseInt(dayMatch[1], 10)
    if (days <= 3) return 'urgent'
    if (days <= 60) return 'moderate'
    return 'flexible'
  }
  return 'moderate'
}

const URGENCY_SORT_ORDER = { urgent: 0, moderate: 1, flexible: 2 }

const STATE_ABBREV_TO_NAME = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia'
}

const REGULATORY_COLORS = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
}

function getRegulatoryImportance(rank) {
  if (rank <= 2) return 'high'
  if (rank <= 4) return 'medium'
  return 'low'
}

function stateMatches(stricterStateEntry, clientState) {
  if (!clientState) return false
  const stateName = stricterStateEntry.split('(')[0].trim().toLowerCase()
  const needle = clientState.trim().toLowerCase()
  if (stateName === needle) return true
  const fullName = STATE_ABBREV_TO_NAME[clientState.trim().toUpperCase()]
  return fullName && stateName === fullName.toLowerCase()
}

function BreachNotificationPanel({ profile, clientState = null, enhancedRegulatoryContext = null }) {
  const [isPreemptionOpen, setIsPreemptionOpen] = useState(false)
  const [isAuthoritiesOpen, setIsAuthoritiesOpen] = useState(false)

  if (!profile?.regulatoryContext?.breachNotification) {
    return null
  }

  // Use enhanced regulatory context if provided, otherwise fall back to profile
  const regulatoryContext = enhancedRegulatoryContext || profile.regulatoryContext
  const bn = regulatoryContext.breachNotification || profile.regulatoryContext.breachNotification
  const notificationOrder = [...(bn.notificationOrder || [])].sort((a, b) => {
    const aUrgency = URGENCY_SORT_ORDER[getTimelineUrgency(a.timeline)] ?? 2
    const bUrgency = URGENCY_SORT_ORDER[getTimelineUrgency(b.timeline)] ?? 2
    if (aUrgency !== bUrgency) return aUrgency - bUrgency
    return a.rank - b.rank
  })
  const primaryAuthority = bn.primaryAuthority
  const secondaryAuthority = bn.secondaryAuthority
  const additionalAuthorities = bn.additionalAuthorities || []
  const statePreemption = bn.statePreemption

  // Check if client's state is in the "stricter states" list
  // Match on the state name portion (before parenthesis) to avoid substring false positives
  // e.g. "AL" should not match "California (CCPA)" just because "california" contains "al"
  const clientStateMatch = clientState && statePreemption?.stricterStates?.find(s =>
    stateMatches(s, clientState)
  )

  return (
    <div className="card p-6 mt-6">
      <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2">
          <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Breach Notification Requirements
        </h3>

        {/* Timeline Badge */}
        {bn.timelineBadge && (
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {bn.timelineBadge}
          </span>
        )}
      </div>

      {/* State-Specific Callout */}
      {clientStateMatch && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                {clientState} has stricter requirements
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                {clientStateMatch}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Notification Order - Numbered Sequence */}
      {notificationOrder.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Required Notification Sequence
          </h4>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-600 dark:text-gray-300">Regulatory weight:</span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400 dark:bg-red-500"></span>
              High
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400 dark:bg-amber-500"></span>
              Moderate
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-gray-400 dark:bg-gray-500"></span>
              Supplementary
            </span>
          </div>
          <div className="space-y-3">
            {notificationOrder.map((notification, idx) => (
              <div
                key={notification.rank}
                className={`flex items-start p-3 rounded-lg border ${
                  notification.required
                    ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-700/50'
                }`}
              >
                {/* Position number — colored by regulatory importance */}
                <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${
                  REGULATORY_COLORS[getRegulatoryImportance(notification.rank)]
                }`}>
                  {idx + 1}
                </div>

                <div className="flex-grow min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      {notification.entity}
                    </p>
                    {!notification.required && (
                      <span className="relative group">
                        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded cursor-help">
                          Conditional
                        </span>
                        <span className="absolute z-50 right-0 bottom-full mb-2 w-56 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          {notification.threshold
                            ? `Required when threshold is met: ${notification.threshold}`
                            : 'Required only when specific conditions are met (see threshold below)'}
                          <span className="absolute right-4 bottom-0 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 dark:bg-gray-700"></span>
                        </span>
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    <span className="font-medium">Timeline:</span> {notification.timeline}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                    {notification.method}
                  </p>
                  {notification.threshold && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 italic">
                      Threshold: {notification.threshold}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Authorities Section - Collapsible */}
      {(primaryAuthority || secondaryAuthority || additionalAuthorities.length > 0) && (
        <div className="mb-4">
          <button
            onClick={() => setIsAuthoritiesOpen(!isAuthoritiesOpen)}
            className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Regulatory Authorities & Citations
              </span>
              {additionalAuthorities.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                  +{additionalAuthorities.length} from assessment
                </span>
              )}
            </div>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${isAuthoritiesOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isAuthoritiesOpen && (
            <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-800/30 rounded-lg space-y-4">
              {primaryAuthority && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Primary Authority
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {primaryAuthority.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 font-mono">
                    {primaryAuthority.citation}
                  </p>
                  {primaryAuthority.url && (
                    <p className="text-xs mt-1">
                      Link:{' '}
                      <a
                        href={primaryAuthority.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-accent-300 hover:underline"
                      >
                        {primaryAuthority.url}
                      </a>
                    </p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Enforcement: {primaryAuthority.enforcementBody}
                  </p>
                </div>
              )}

              {secondaryAuthority && (
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Secondary Authority
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {secondaryAuthority.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 font-mono">
                    {secondaryAuthority.citation}
                  </p>
                  {secondaryAuthority.url && (
                    <p className="text-xs mt-1">
                      Link:{' '}
                      <a
                        href={secondaryAuthority.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-accent-300 hover:underline"
                      >
                        {secondaryAuthority.url}
                      </a>
                    </p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Enforcement: {secondaryAuthority.enforcementBody}
                  </p>
                </div>
              )}

              {/* Additional Authorities from Framework Selection */}
              {additionalAuthorities.length > 0 && (
                <div className={primaryAuthority || secondaryAuthority ? 'pt-3 border-t border-gray-200 dark:border-gray-700' : ''}>
                  <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide mb-3">
                    From Assessment Selections
                  </p>
                  <div className="space-y-3">
                    {additionalAuthorities.map((authority, idx) => (
                      <div key={idx} className="pl-3 border-l-2 border-purple-300 dark:border-purple-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {authority.name}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 font-mono">
                          {authority.citation}
                        </p>
                        {authority.url && (
                          <p className="text-xs mt-1">
                            Link:{' '}
                            <a
                              href={authority.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 dark:text-accent-300 hover:underline"
                            >
                              {authority.url}
                            </a>
                          </p>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          Enforcement: {authority.enforcementBody}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* State Preemption Notes - Collapsible */}
      {statePreemption && (
        <div>
          <button
            onClick={() => setIsPreemptionOpen(!isPreemptionOpen)}
            className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                State Preemption Analysis
              </span>
              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                statePreemption.federalPreempts
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                  : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
              }`}>
                {statePreemption.federalPreempts ? 'Federal Preempts' : 'State Laws Apply'}
              </span>
            </div>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${isPreemptionOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isPreemptionOpen && (
            <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-800/30 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                {statePreemption.notes}
              </p>

              {statePreemption.stricterStates && statePreemption.stricterStates.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    States with Stricter Requirements
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {statePreemption.stricterStates.map((state, idx) => (
                      <span
                        key={idx}
                        className={`px-2 py-1 text-xs rounded-full ${
                          stateMatches(state, clientState)
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-medium'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {state}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  )
}

export default BreachNotificationPanel
